import { useCallback, useEffect, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/ExpenseReportForm'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import messages from './messages'
import { useMutateReviseExpenseReport } from '../../models/useMutateReviseExpenseReport'

interface UseReviseExpenseReportPresenterParams {
  expense: Expense
  initialValues?: Partial<ExpenseReportFormSchema>
  onSuccess: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

export const useReviseExpenseReportPresenter = ({
  expense,
  initialValues,
  onSuccess,
  onSubmittingChange,
}: UseReviseExpenseReportPresenterParams) => {
  const intl = useProcurifyIntl()

  const [selectedLocationId, setSelectedLocationId] = useState<
    Nullable<number>
  >(expense.primaryCategory.id)
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<
    Nullable<number>
  >(initialValues?.department?.id ?? expense.secondaryCategory.id)

  const { mutate, isLoading: isSubmitting } = useMutateReviseExpenseReport()

  useEffect(() => {
    onSubmittingChange?.(isSubmitting)
  }, [isSubmitting, onSubmittingChange])

  const handleLocationChange = useCallback((locationId: number) => {
    setSelectedLocationId(locationId)
    setSelectedDepartmentId(null)
  }, [])

  const handleDepartmentChange = useCallback((departmentId: number) => {
    setSelectedDepartmentId(departmentId)
  }, [])

  const handleSubmit = useCallback(
    async (values: ExpenseReportFormSchema) => {
      try {
        if (!values.department?.id || !selectedDepartmentId) {
          return toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children: intl.formatMessage(
              messages.missingDepartmentErrorMessage
            ),
          })
        }

        await mutate({
          expenseId: expense.id,
          uuid: expense.uuid,
          payload: {
            title: values.title,
            department: values.department?.id ?? selectedDepartmentId,
            travel: values.travel?.id ?? null,
            notes: values.notes,
            next_approver: values.nextApprover?.id ?? 0,
          },
        })

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage, {
            id: expense.id,
          }),
        })

        onSuccess()
      } catch (err) {
        const errorMessage = getMessageFromError(err)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [expense.id, expense.uuid, intl, mutate, onSuccess, selectedDepartmentId]
  )

  return {
    states: {
      isSubmitting,
      selectedLocationId,
      selectedDepartmentId,
    },
    methods: {
      handleSubmit,
      handleLocationChange,
      handleDepartmentChange,
    },
  }
}
